import { useCallback, useEffect, useMemo } from "react";

import type { GetFlotiqStores, Map } from "@locators/components/shared/hooks/useFlotiqStores/useFlotiqStores.types";
import { useGoogleMapsStoreLocator } from "@locators/components/shared/hooks/useGoogleMapsStoreLocator";
import { useNaverMapsStoreLocator } from "@locators/components/shared/hooks/useNaverMapsStoreLocator/useNaverMapsStoreLocator";
import type { StoreLocatorReturnType } from "@locators/components/shared/hooks/useStoreLocator";
import { useTimedToggle } from "@locators/components/shared/hooks/useTimedToggle";
import { useYandexMapsStoreLocator } from "@locators/components/shared/hooks/useYandexMapsStoreLocator/useYandexMapsStoreLocator";

import { useConfig } from "@jti/app-context/useConfig";
import type { FlotiqStore, MapProvider } from "@jti/fe-common";

const SECOND = 1000;
const DEFAULT_MAP_INACTIVITY_TIMEOUT = 3 * SECOND;

type SimpleStoreLocatorData = {
  stores: FlotiqStore[];
  activeStoreId: string;
  getStores: (map: Map) => Promise<void> | void;
  handleSelectStore: (storeId: string) => void;
  getUserMarkerPosition: () => void;
  userMarkerPosition: { lat: number; lng: number } | undefined;
};

type SimpleStoreLocatorArgs = {
  defaultLat: string;
  defaultLng: string;
  defaultZoom?: number;
  isOpeningHoursBreaksEnabled?: boolean;
  mapProviderOverride?: MapProvider;
  mapInactivityTimeout?: number;
  theme: "dark" | "light";
  storesData: SimpleStoreLocatorData;
  defaultStoreIcon: string;
  activeStoreIcon: string;
  translationPrefix: string;
};

export const useSimpleStoreLocator = ({
  defaultLat,
  defaultLng,
  defaultZoom,
  isOpeningHoursBreaksEnabled = false,
  mapProviderOverride,
  mapInactivityTimeout = DEFAULT_MAP_INACTIVITY_TIMEOUT,
  theme,
  storesData,
  defaultStoreIcon,
  activeStoreIcon,
  translationPrefix,
}: SimpleStoreLocatorArgs): StoreLocatorReturnType => {
  const { active: isMapActive, toggle: toggleIsMapActive, off: setMapInactive } = useTimedToggle(mapInactivityTimeout);
  const { locationServiceMapProvider } = useConfig();

  const effectiveMapProvider: MapProvider = mapProviderOverride ?? locationServiceMapProvider;

  const handleLoadStores: GetFlotiqStores = useCallback(
    async (map) => {
      if (!map) {
        return;
      }

      await storesData.getStores(map);
    },
    [storesData]
  );

  const mapStoreLocator = useMemo(() => {
    switch (effectiveMapProvider) {
      case "google":
        return useGoogleMapsStoreLocator;
      case "naver":
        return useNaverMapsStoreLocator;
      case "yandex":
        return useYandexMapsStoreLocator;
      default:
        return useGoogleMapsStoreLocator;
    }
  }, [effectiveMapProvider]);

  const storeLocatorMapData = mapStoreLocator({
    defaultLat,
    defaultLng,
    defaultZoom,
    mapTheme: theme,
    activeStoreId: storesData.activeStoreId,
    handleSelectStore: storesData.handleSelectStore,
    handleLoadFlotiqStores: handleLoadStores,
    defaultStoreIcon,
    activeStoreIcon,
    isSearchVisible: true,
    onActivity: toggleIsMapActive,
    onTap: setMapInactive,
  });

  useEffect(() => {
    storeLocatorMapData.handleLoadMarkers(storesData.stores || []);

    if (effectiveMapProvider === "naver" && storesData.activeStoreId) {
      storeLocatorMapData.handleSelectStoreFromList?.(storesData.activeStoreId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storesData.stores, defaultStoreIcon]);

  useEffect(() => {
    if (storesData.userMarkerPosition) {
      storeLocatorMapData.handleLoadUserMarker(storesData.userMarkerPosition.lat, storesData.userMarkerPosition.lng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storesData.userMarkerPosition]);

  const simpleStoreLocatorReturn: StoreLocatorReturnType = {
    ...storeLocatorMapData,
    flotiqStores: storesData.stores,
    activeStoreId: storesData.activeStoreId,
    handleSelectStore: storeLocatorMapData?.handleSelectStoreFromList ?? storesData.handleSelectStore,
    getUserMarkerPosition: storesData.getUserMarkerPosition,
    userMarkerPosition: storesData.userMarkerPosition,
    isNoMatchingFlotiqStores: false,

    filters: [],
    newFilters: [],
    newFiltersNumber: 0,
    selectedFilters: {},
    isFiltersOpened: false,
    isFiltersAvailable: false,
    selectedFiltersNumber: 0,
    filtersEnabled: false,
    getFlotiqStores: handleLoadStores,
    handleToggleFilters: () => {},
    handleResetFilters: () => {},
    handleChangeFilters: () => {},
    isNoStoresFilters: { value: false, type: "" },
    handleDropdownRestart: () => {},
    handleDropdownSelect: () => {},
    checkFiltersStatus: () => false,
    changeFilters: () => {},
    applyFilters: () => {},
    handleApplyOpenNowCheckbox: () => {},
    activeFilters: {},

    isOpenNowFilterEnabled: false,
    isProductColorsFilterEnabled: false,
    isProductFlavoursFilterEnabled: false,
    isProductFlavoursLYOFilterEnabled: false,
    isProductTypesFilterEnabled: false,
    isStoreServicesFilterEnabled: false,
    isStoreTypeFilterEnabled: false,

    isSearchVisible: true,
    isOpeningHoursEnabled: true,
    isOpeningHoursBreaksEnabled,
    isStoreCardAddressEnabled: true,
    isAvailableDevicesEnabled: false,
    isAvailableSticksEnabled: false,
    isMultiBrandSticksEnabled: false,
    isGeneralAnnouncementEnabled: false,
    isAvailableServicesEnabled: false,
    isVendingMachineEnabled: false,
    isVendingMachineTipEnabled: false,
    isNearbyShopsEnabled: false,
    isAsianAddressFormatEnabled: false,
    isInputExtraTextVisible: false,
    isMultipleFiltersSectionOpenEnabled: false,
    isMapActive,

    storeDetailsPage: "",
    storeDetailsPageTarget: "_blank",
    onChooseStore: undefined,
    defaultZoom,
    translationPrefix,
  };

  return simpleStoreLocatorReturn;
};
